/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, inject, ChangeDetectionStrategy, output, signal } from '@angular/core';
import {
  FormControl,
  FormControlDirective,
  FormControlName,
  NgControl,
  NgModel,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  convertCzDateToIso,
  convertSimplifiedDate,
  DomHandlerService,
  parseStringToDate,
  restrictDatepickerInput,
  simplifyDate,
  uniqueId,
} from '@shared/utils';
import { NEVER, merge } from 'rxjs';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../directives/base-form-control.directive';
import {
  DatePickerFormControlDirective,
  DatePickerFormControlDirectiveInputs,
  DatePickerFormControlDirectiveOutputs,
} from '../directives/datepicker-form-control.directive';
import {
  DropdownFormControlDirective,
  DropdownFormControlDirectiveInputs,
} from '../directives/dropdown-form-control.directive';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

import { DIALOG_BODY_CLASS } from '@shared/utils';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { DatepickerClearIconFixDirective } from '../directives/datepicker-clear-icon-fix.directive';
import { NoopValueAccessorDirective } from '../directives/noop-value-accessor.directive';
import { FormComponent } from '../form/form.component';
import { FormConnectorService } from '../forms.utils';

const CALENDAR_OPEN_HEIGHT_PX = 490;

export enum DateType {
  Date = 'date',
  DateAsString = 'dateAsString',
}

@Component({
  selector: 'ui-datepicker',
  templateUrl: './datepicker.component.html',
  host: { class: 'block mb-4' },
  imports: [DatePickerModule, DatepickerClearIconFixDirective, ReactiveFormsModule, InputWrapperComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    NoopValueAccessorDirective,
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
    },
    {
      directive: DatePickerFormControlDirective,
      inputs: DatePickerFormControlDirectiveInputs,
      outputs: DatePickerFormControlDirectiveOutputs,
    },
  ],
})
export class DatepickerComponent {
  private _form = inject(FormComponent, { optional: true, skipSelf: true });
  protected _formConnectorService = inject(FormConnectorService, { optional: true, skipSelf: true });
  protected noopValueAccessor = inject<NoopValueAccessorDirective<any>>(NoopValueAccessorDirective);
  readonly handleChange = output<any>();

  restrictInput = restrictDatepickerInput;

  submitted = false;
  randomInputId = uniqueId();
  // signál: pozice se mění i v setTimeout, kde by se bez zone.js nevykreslila
  readonly panelStyle = signal<Record<string, string>>({});

  private _el = inject(ElementRef);
  private _dom = inject(DomHandlerService);

  ngControl = injectNgControl();
  innerControl = new FormControl();
  value: any;

  base = inject(BaseFormControlDirective);
  dropdown = inject(DropdownFormControlDirective);
  datePicker = inject(DatePickerFormControlDirective);

  constructor() {
    this.noopValueAccessor.value.subscribe((v) => {
      this.writeValue(v);
    });

    merge<boolean[]>(this._formConnectorService?.submitted$ ?? NEVER, this._form?.getSubmitted() ?? NEVER)
      .pipe(takeUntilDestroyed())
      .subscribe((_) => (this.submitted = _));
  }

  writeValue(value: unknown): void {
    switch (this.datePicker.dataType()) {
      case DateType.Date: {
        this.value = value;
        break;
      }
      case DateType.DateAsString: {
        this.value = parseStringToDate(value as string);
        break;
      }
    }
    this.innerControl.setValue(this.value as Date, { emitEvent: false });
  }

  fireHandleChange(value: any) {
    switch (this.datePicker.dataType()) {
      case DateType.Date: {
        this.value = value;
        break;
      }
      case DateType.DateAsString: {
        if (value) {
          this.value = simplifyDate(value);
        } else {
          this.value = value;
        }
        break;
      }
    }
    this.handleChange.emit(this.value);
    this.noopValueAccessor.valueChange(this.value);
  }

  fireControlChange() {
    const value = this.innerControl.value;
    this.fireHandleChange(value);
  }

  get isInDialog() {
    const bodyEl = this._dom.bodyEl;
    return bodyEl.classList.contains(DIALOG_BODY_CLASS);
  }

  handleOnMonthChange() {
    const scrollTop = this._dom.htmlEl.scrollTop || 0;

    setTimeout(() => {
      if (this.isInDialog) this._dom.htmlEl.scrollTop = scrollTop;
    });

    setTimeout(() => {
      this._setTopPosition(1);
    }, 50);
  }

  // for datepickers in dialogs, we need to handle the top position differently
  setCorrectCalendarTopPosition() {
    this._setTopPosition(0);
    // TODO: The 'emit' function requires a mandatory null argument
    this.datePicker.handleShow.emit();
  }

  handleOnClose() {
    this.panelStyle.set({});
  }

  handleOnEnter(event: any) {
    this.processInsertExplicitDate(event.target.value);
  }

  // #region -------------------------------------------------------- Private Methods --------------------------------------------------------
  private processInsertExplicitDate(value: string) {
    let processedValue = value;

    // Zkontrolujeme, jestli je datum ve zjednodušeném formátu (ddMMyy)
    const simplifiedDate = convertSimplifiedDate(processedValue);
    if (simplifiedDate) {
      processedValue = simplifiedDate; // Pokud je, použijeme převedený plný formát
    }

    // Převod z cz formátu na ISO
    const isoDate = convertCzDateToIso(processedValue);
    const date = isoDate ? parseStringToDate(isoDate) : null;

    this.writeValue(isoDate);
    this.fireControlChange();
  }
  private _setTopPosition(diff = 0) {
    if (!this.isInDialog) return;

    const y = (this._el.nativeElement as HTMLElement).getBoundingClientRect().top;
    const inputHeight = (this._el.nativeElement as HTMLElement).getBoundingClientRect().height;
    const panelTop = this._dom.htmlEl.scrollTop + diff;
    if (y < CALENDAR_OPEN_HEIGHT_PX)
      this.panelStyle.set({
        top: `${this.datePicker.timeOnly() ? panelTop + y + inputHeight : panelTop}px`,
      });
  }

  // private updateMinDateTo(): void {
  //   const fromDate = this.innerFG.get(this.datePicker.fromKey)?.value;
  //   this.calendarRefTo.minDate = fromDate ? new Date(fromDate) : null;
  // }

  // private updateMaxDateFrom(): void {
  //   const toDate = this.innerFG.get(this.datePicker.toKey)?.value;
  //   this.calendarRefFrom.maxDate = toDate ? new Date(toDate) : null;
  // }
  //#endregion ------------------------------------------------------ Private Methods --------------------------------------------------------
}

export function injectNgControl() {
  const ngControl = inject(NgControl, { self: true, optional: true });

  if (!ngControl) throw new Error('...');

  if (
    ngControl instanceof FormControlDirective ||
    ngControl instanceof FormControlName ||
    ngControl instanceof NgModel
  ) {
    return ngControl;
  }

  throw new Error('...');
}
